import h337 from 'heatmap.js';
import debounce from 'lodash/debounce';
import IframeFullDocWithRef from 'modules/secureIframe/IframeFullDocWithRef';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircleSpinner } from 'ui/spinner';
import { useDynamicHeatmapContext, useDynamicHeatmapDrawerContext } from '../../context';
import { createHeatmap, extractContentFromJqueryElem, maxBy } from '../../utils';
import { TEMPLATE_PREVIEW_HEADER } from './constants';
import { HeaderBanner } from './HeaderBanner';
import {
  ContentWrapper,
  FullScreenAnalyticsCard,
  HeatmapCardHeader,
  HeatmapViz,
  InnerWrapper,
  LoadingContainer,
  OuterWrapper,
  PreviewContent,
  PreviewContentBody,
  ScrollablePane,
} from './TemplatePreview.styles';
import { TemplatePreviewButtons } from './TemplatePreviewButtons';
import { TooltipOverlay } from './TooltipOverlay';
import { UserInfoPane } from './UserInfoPane';

const getLinkStatsWithId = (linkStats: any[]) =>
  linkStats.map((el, idx) => ({
    ...el,
    id: idx,
  }));

export const TemplatePreview = () => {
  const {
    contentId,
    messageContent,
    userData,
    isLoading,
    isFullscreen,
    dynamicLinkStats,
    rawLinkData,
    augmentedLinkData,
    setAugmentedLinkData,
    setTemplateUrlData,
    setRawLinkData,
    loadedIframe,
    setLoadedIframe,
  } = useDynamicHeatmapContext();

  const [iFrameHeight, setIFrameHeight] = useState(0);
  const [iFrameWidth, setIframeWidth] = useState('100%');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const heatmapHeaderRef = useRef<HTMLDivElement>(null);
  const previewBannerRef = useRef<HTMLDivElement>(null);
  const heatmapVisualizationRef = useRef<HTMLDivElement>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [maxCount, setMaxCount] = useState();
  const [showHeatmap, setShowHeatmap] = useState(true);
  const { isExpanded } = useDynamicHeatmapDrawerContext();
  const memoizedDynamicLinkStats = useMemo(
    () => dynamicLinkStats,
    [JSON.stringify(dynamicLinkStats)],
  );
  const memoizedRawLinkData = useMemo(() => rawLinkData, [JSON.stringify(rawLinkData)]);

  useEffect(() => {
    if (isLoading) {
      setShowHeatmap(false);
    }
  }, [isLoading]);

  useEffect(() => {
    setIFrameHeight(-1);
    setTimeout(() => {
      setIFrameHeight(0);
    }, 251);
  }, [isExpanded]);

  const handleResize = useCallback(
    debounce(() => {
      setLoadedIframe(loadedIframe + 1);
    }, 200),
    [loadedIframe],
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    const iframe = iframeRef?.current;

    if (
      rawLinkData.length > 0 &&
      dynamicLinkStats.length > 0 &&
      iframe?.contentWindow?.document?.body
    ) {
      const aggregatedUrlData = rawLinkData.reduce((acc, curr) => {
        const { trackingIds = [] } = curr;
        const extendedTrackInfo = trackingIds.map((trackingId: string) => {
          const urlElem = iframe?.contentWindow?.document.body.querySelector(
            `[data-itr="${trackingId}"]`,
          );
          const linkStat =
            dynamicLinkStats.find((linkStat) => linkStat.trackingId === trackingId) || {};
          const isTemplateLink = trackingId.split('-')?.[0] === linkStat?.contentId?.toString();
          return urlElem
            ? { ...extractContentFromJqueryElem(urlElem), ...linkStat, isTemplateLink }
            : { trackingId, ...linkStat, isTemplateLink };
        });
        return [...acc, { ...curr, extendedTrackInfo }];
      }, []);
      setRawLinkData(aggregatedUrlData);
    }
  }, [loadedIframe, memoizedRawLinkData, memoizedDynamicLinkStats]);

  useEffect(() => {
    setShowHeatmap(true);

    const iframe = iframeRef?.current;
    const augmentedLinks: any[] = [];
    const tableLinks = [];
    if (dynamicLinkStats && iframe?.contentWindow?.document?.body) {
      const getSum = (key: string) =>
        dynamicLinkStats.map((el: any) => el[key]).reduce((memo, num) => memo + num, 0);
      const counts = {
        total: getSum('totalCount'),
        unique: getSum('uniqueCount'),
      };

      if (dynamicLinkStats.length > 0) {
        setMaxCount(
          maxBy(dynamicLinkStats, (link: any) => {
            return link.totalCount;
          }).totalCount,
        );
      }
      setTotalCount(counts.total);
      getLinkStatsWithId(dynamicLinkStats).forEach((linkData) => {
        const linkContentId = linkData?.contentId;
        const urlElem = iframe?.contentWindow?.document.body.querySelector(
          `[data-itr="${linkData.trackingId}"]`,
        );

        if (urlElem && linkContentId === contentId) {
          const extended = { ...extractContentFromJqueryElem(urlElem), ...linkData };
          augmentedLinks.push(extended);
          tableLinks.push(extended);
        } else {
          tableLinks.push(linkData);
        }
      });
    } else if (dynamicLinkStats) {
      tableLinks.push(...getLinkStatsWithId(dynamicLinkStats));
    }
    setTemplateUrlData(tableLinks);
    setAugmentedLinkData(augmentedLinks);
  }, [memoizedDynamicLinkStats, loadedIframe, contentId]);

  useEffect(() => {
    if (heatmapVisualizationRef.current) {
      const heatmapConfig = {
        container: heatmapVisualizationRef.current,
        radius: 50,
        maxOpacity: 0.5,
        minOpacity: 0,
        blur: 0.75,
        legend: {
          position: 'tr',
          title: 'test',
        },
      };
      const iframe = iframeRef?.current;
      const wrapper = document.getElementById('heatmap-visualization');
      if (wrapper && wrapper?.firstChild) {
        wrapper.removeChild(wrapper.firstChild);
      }

      if (iframe?.contentWindow?.document?.body?.innerHTML) {
        const createdHeatmap = h337.create(heatmapConfig);
        createHeatmap(augmentedLinkData, createdHeatmap, maxCount, true);
      }
    }
  }, [heatmapVisualizationRef, augmentedLinkData]);

  return (
    <FullScreenAnalyticsCard $isFullscreen={isFullscreen}>
      <HeatmapCardHeader $isFullscreen={isFullscreen} ref={heatmapHeaderRef}>
        <div data-test={TEMPLATE_PREVIEW_HEADER}>Heatmap</div>
        <TemplatePreviewButtons />
      </HeatmapCardHeader>

      {userData !== '{}' && <HeaderBanner ref={previewBannerRef} />}

      <ContentWrapper
        $isExpanded={isExpanded}
        $previewHeaderVisible={userData !== '{}'}
        $isFullscreen={isFullscreen}
        $heatmapHeaderHeight={heatmapHeaderRef?.current?.offsetHeight}
        $previewBannerHeight={previewBannerRef?.current?.offsetHeight}
      >
        <OuterWrapper $isExpanded={isExpanded}>
          <InnerWrapper $isExpanded={isExpanded}>
            <ScrollablePane
              $previewHeaderVisible={userData !== '{}'}
              $isFullscreen={isFullscreen}
              $heatmapHeaderHeight={heatmapHeaderRef?.current?.offsetHeight}
              $previewBannerHeight={previewBannerRef?.current?.offsetHeight}
            >
              <UserInfoPane />
            </ScrollablePane>
          </InnerWrapper>
        </OuterWrapper>
        <ScrollablePane
          $previewHeaderVisible={userData !== '{}'}
          $isFullscreen={isFullscreen}
          $heatmapHeaderHeight={heatmapHeaderRef?.current?.offsetHeight}
          $previewBannerHeight={previewBannerRef?.current?.offsetHeight}
        >
          <PreviewContent data-test="heatmap-email-preview">
            <PreviewContentBody ref={previewRef} style={{ width: iFrameWidth }}>
              {(isLoading || !showHeatmap) && (
                <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
                  <LoadingContainer $isFullscreen={isFullscreen}>
                    <CircleSpinner />
                  </LoadingContainer>
                </div>
              )}
              <IframeFullDocWithRef
                loadFunc={() => {
                  const iframe = iframeRef?.current;
                  if (
                    iframe?.contentWindow?.document?.body?.scrollWidth &&
                    previewRef?.current?.scrollWidth &&
                    iframe?.contentWindow?.document?.body?.scrollWidth >
                      previewRef?.current?.scrollWidth
                  ) {
                    setIframeWidth(`${iframe?.contentWindow?.document?.body?.scrollWidth}px`);
                  } else {
                    setIframeWidth('100%');
                  }
                  setLoadedIframe(loadedIframe + 1);
                }}
                ref={iframeRef}
                height={iFrameHeight}
              >
                {messageContent}
              </IframeFullDocWithRef>
              {showHeatmap && (
                <HeatmapViz
                  ref={heatmapVisualizationRef}
                  data-test="heatmap-visual"
                  id="heatmap-visualization"
                  style={{ height: '100%', width: iFrameWidth }}
                />
              )}
              {showHeatmap && (
                <TooltipOverlay
                  overlayWidth={iFrameWidth}
                  augmentedLinkData={augmentedLinkData}
                  totalCount={totalCount}
                />
              )}
            </PreviewContentBody>
          </PreviewContent>
        </ScrollablePane>
      </ContentWrapper>
    </FullScreenAnalyticsCard>
  );
};
